/**
 * Data Quality API Client — Great Expectations / Pandera suite
 *
 * Exposes the five GX data-quality endpoints as typed async functions.
 * All endpoints accept a claimId and POST an empty body to the case-scoped route.
 */

import axios from '@/lib/http';
import { getAuthToken } from '@/utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Authenticated POST using the global Clerk token getter */
async function authPost<T>(path: string, body: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = await getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const { data } = await axios.post(`${BACKEND_URL}${path}`, body, { headers });
  return data;
}

// ============================================================================
// TYPES — Response shapes from /api/v1/data-quality/* endpoints
// ============================================================================

/** Single validation issue from Pandera/GX validation */
export interface ValidationIssue {
  message: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  column?: string;
  row_index?: number;
  expectation_type?: string;
}

/** Result from POST /api/v1/data-quality/validate (Pandera schema validation) */
export interface ValidateResult {
  success: boolean;
  is_valid: boolean;
  quality_score: number;
  quality_grade: 'A' | 'B' | 'C' | 'D' | 'F';
  total_rows: number;
  valid_rows: number;
  error_count: number;
  warning_count: number;
  issues: ValidationIssue[];
  timestamp: string;
}

/** Single expectation result from Great Expectations */
export interface ExpectationResult {
  expectation_type: string;
  success: boolean;
  column?: string;
  observed_value?: unknown;
  unexpected_count?: number;
  unexpected_percent?: number;
  details?: Record<string, unknown>;
}

/** Result from POST /api/v1/data-quality/expectations (Great Expectations) */
export interface GXValidateResult {
  success: boolean;
  data_type: 'transactions' | 'periods';
  total_expectations: number;
  passed_expectations: number;
  failed_expectations: number;
  quality_score: number;
  quality_grade: 'A' | 'B' | 'C' | 'D' | 'F';
  results: ExpectationResult[];
  report?: {
    title: string;
    overall_status: string;
    quality_score: number;
    quality_grade: string;
    summary: Record<string, unknown>;
  };
}

/** Column-level statistical profile */
export interface ColumnProfile {
  name: string;
  type: string;
  count: number;
  missing: number;
  missing_pct: number;
  unique: number;
  unique_pct: number;
  mean?: number;
  std?: number;
  min?: number;
  max?: number;
  quartiles?: {
    q25: number;
    q50: number;
    q75: number;
  };
  anomalies?: Array<{
    type: string;
    severity: 'high' | 'medium' | 'low';
    message: string;
  }>;
}

/** Result from POST /api/v1/data-quality/profile (Statistical profiling) */
export interface ProfileResult {
  success: boolean;
  profile_type: 'transactions' | 'periods';
  row_count: number;
  column_count: number;
  memory_size_mb: number;
  total_anomalies: number;
  high_severity_anomalies: number;
  columns: Record<string, ColumnProfile>;
  correlations?: Record<string, Record<string, number>>;
  metadata: {
    generated_at?: string;
    title?: string;
    error?: string;
  };
}

/** Cross-document validation issue */
export interface CrossValidationIssue {
  check: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  message: string;
  documents?: string[];
  field?: string;
  expected?: unknown;
  actual?: unknown;
}

/** Duplicate transaction group detected across documents */
export interface DuplicateGroup {
  hash: string;
  count: number;
  transactions: Array<{
    document: string;
    date: string;
    description: string;
    amount: number;
  }>;
}

/** Result from POST /api/v1/data-quality/cross-validate */
export interface CrossValidateResult {
  success: boolean;
  is_valid: boolean;
  documents_checked: number;
  checks_performed: string[];
  issue_count: number;
  issues_by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  issues: CrossValidationIssue[];
  duplicate_groups_count: number;
  duplicate_groups: DuplicateGroup[];
  reconciliation_summary: {
    total_discrepancy?: number;
    document_totals?: Record<string, number>;
    note?: string;
  };
}

/** Validation summary within quality report */
export interface ValidationSummary {
  source: string;
  is_valid: boolean;
  score: number;
  total_checks: number;
  passed_checks: number;
  failed_checks: number;
  critical_issues: number;
  warnings: number;
}

/** Result from POST /api/v1/data-quality/report (Comprehensive quality report) */
export interface DataQualityReport {
  success: boolean;
  report_id: string;
  overall_grade: 'A' | 'B' | 'C' | 'D' | 'F';
  overall_score: number;
  is_court_ready: boolean;
  executive_summary: string;
  validation_summaries: ValidationSummary[];
  recommendations: string[];
  issues_by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  formatted_report?: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Validate data against Pandera schemas.
 * POST /api/v1/data-quality/validate
 */
export async function fetchDataQualityValidate(claimId: string): Promise<ValidateResult> {
  return authPost<ValidateResult>(`/api/v1/data-quality/validate?claim_id=${encodeURIComponent(claimId)}`, {
    data_type: 'transactions',
    layer: 'extraction',
    strict_mode: false,
  });
}

/**
 * Validate using Great Expectations business rules.
 * POST /api/v1/data-quality/expectations
 */
export async function fetchExpectations(claimId: string): Promise<GXValidateResult> {
  return authPost<GXValidateResult>(`/api/v1/data-quality/expectations?claim_id=${encodeURIComponent(claimId)}`, {
    data_type: 'transactions',
  });
}

/**
 * Generate statistical profile.
 * POST /api/v1/data-quality/profile
 */
export async function fetchProfile(claimId: string): Promise<ProfileResult> {
  return authPost<ProfileResult>(`/api/v1/data-quality/profile?claim_id=${encodeURIComponent(claimId)}`, {
    data_type: 'transactions',
    use_ydata: false,
    title: `Data Profile for ${claimId}`,
  });
}

/**
 * Cross-document validation.
 * POST /api/v1/data-quality/cross-validate
 */
export async function fetchCrossValidate(claimId: string): Promise<CrossValidateResult> {
  return authPost<CrossValidateResult>(`/api/v1/data-quality/cross-validate?claim_id=${encodeURIComponent(claimId)}`, {
    checks: ['balance_reconciliation', 'duplicate_detection', 'period_coverage'],
  });
}

/**
 * Generate comprehensive quality report.
 * POST /api/v1/data-quality/report
 */
export async function fetchDataQualityReport(claimId: string): Promise<DataQualityReport> {
  return authPost<DataQualityReport>(`/api/v1/data-quality/report?claim_id=${encodeURIComponent(claimId)}`, {
    case_id: claimId,
    format: 'json',
  });
}
